import { useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ArrowUpTrayIcon,
  CheckIcon,
  InformationCircleIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import TypeAhead, { TypeAheadOption } from '../../../components/TypeAhead';
import Toggle from '../../../components/Toggle';
import { Drawer } from '../../../components/ui/Drawer';
import { Tooltip } from '../../../components/ui/Tooltip';
import contractsService from '../../../services/compliance/contracts.service';
import contractCategoriesService from '../../../services/compliance/contractCategories.service';
import contractDepartmentsService from '../../../services/compliance/contractDepartments.service';
import thirdPartiesService from '../../../services/compliance/thirdParties.service';
import { AzureADUser } from '../../../services/permissions.service';
import { useComplianceAccess } from '../../../hooks/useComplianceAccess';
import ContractDocumentsPanel from './ContractDocumentsPanel';
import ContractNotesPanel from './ContractNotesPanel';
import EntityAuditPanel from '../audit/EntityAuditPanel';
import {
  effectiveStatus,
  endDateForTerm,
  formatTermLength,
  isMonthToMonth,
  suggestedMonthlyCost,
  withMonthlyCostSuggestion,
} from './contractTerm';
import { formatDateOnly } from './contractAttention';
import {
  Contract,
  ContractCategory,
  ContractDepartment,
  ContractCreate,
  ContractRecurrence,
  ContractStatus,
  ContractTermUnit,
  ContractUpdate,
  ThirdParty,
} from '../../../types/compliance';

type Mode = 'create' | 'edit';

export interface ContractDrawerProps {
  mode: Mode;
  contractId: string | null;
  open: boolean;
  /** AD users loaded once by parent; drawer maps to TypeAhead options. */
  adUsers: AzureADUser[];
  onClose: () => void;
  onSaved: (c: Contract) => void;
  onDeleted: () => void;
  /** Fires when the user clicks "Bulk Import" in create mode. The parent is
   *  expected to close this drawer and open ContractImportDrawer. */
  onBulkImport?: () => void;
}

type FormState = {
  third_party_id: string;
  title: string;
  category: string;
  department: string;
  status: ContractStatus;
  contract_value: string;
  /** Monthly Cost as typed; '' means not given. Prefilled from Total ÷ months. */
  monthly_cost: string;
  /** '' means not recurring. 'monthly' | 'quarterly' | 'annually' otherwise. */
  recurrence: '' | ContractRecurrence;
  start_date: string;
  end_date: string;
  /** Contract Length number as typed; '' means not given. */
  term_length: string;
  term_unit: ContractTermUnit;
  renewal_action_date: string;
  owner_oid: string;
  renewal_owner_oid: string;
  notice_period_days: string;
  auto_renewal: boolean;
  termination_for_convenience: boolean;
  termination_notice_days: string;
  dpa_required: boolean;
  dpa_executed: boolean;
  insurance_required: boolean;
  notes: string;
};

const STATUS_OPTIONS: { value: ContractStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'pending_renewal', label: 'Pending Renewal' },
];

const STATUS_BADGE: Record<ContractStatus, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  active: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  expired: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  terminated: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  pending_renewal: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

const TABS = ['overview', 'obligations', 'documents', 'notes', 'audit'] as const;
type TabKey = (typeof TABS)[number];
const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  obligations: 'Obligations',
  documents: 'Documents',
  notes: 'Notes',
  audit: 'Audit',
};

const emptyForm: FormState = {
  third_party_id: '',
  title: '',
  category: '',
  department: '',
  status: 'active',
  contract_value: '',
  monthly_cost: '',
  recurrence: '',
  start_date: '',
  end_date: '',
  term_length: '',
  term_unit: 'years',
  renewal_action_date: '',
  owner_oid: '',
  renewal_owner_oid: '',
  notice_period_days: '',
  auto_renewal: false,
  termination_for_convenience: false,
  termination_notice_days: '',
  dpa_required: false,
  dpa_executed: false,
  insurance_required: false,
  notes: '',
};

function contractToFormState(c: Contract): FormState {
  return {
    third_party_id: c.third_party_id,
    title: c.title,
    category: c.category,
    department: c.department,
    status: c.status,
    contract_value: c.contract_value ?? '',
    monthly_cost: c.monthly_cost ?? '',
    recurrence: c.recurrence ?? '',
    start_date: c.start_date,
    end_date: c.end_date,
    term_length: c.term_length === null ? '' : String(c.term_length),
    term_unit: c.term_unit ?? 'years',
    renewal_action_date: c.renewal_action_date ?? '',
    owner_oid: c.owner_oid,
    renewal_owner_oid: c.renewal_owner_oid ?? '',
    notice_period_days: c.notice_period_days === null ? '' : String(c.notice_period_days),
    auto_renewal: c.auto_renewal,
    termination_for_convenience: c.termination_for_convenience,
    termination_notice_days:
      c.termination_notice_days === null ? '' : String(c.termination_notice_days),
    dpa_required: c.dpa_required,
    dpa_executed: c.dpa_executed,
    insurance_required: c.insurance_required,
    notes: c.notes ?? '',
  };
}

/** Small "i" icon that explains a field on hover/focus. */
function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip content={text} placement="top" zIndex={100001}>
      <span
        tabIndex={0}
        aria-label={text}
        className="ml-1 inline-flex align-middle text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <InformationCircleIcon className="h-4 w-4" />
      </span>
    </Tooltip>
  );
}

export default function ContractDrawer({
  mode: initialMode,
  contractId: initialId,
  open,
  adUsers,
  onClose,
  onSaved,
  onDeleted,
  onBulkImport,
}: ContractDrawerProps) {
  // Internal mode/id — flips from 'create' to 'edit' after first save.
  const [mode, setMode] = useState<Mode>(initialMode);
  const [currentId, setCurrentId] = useState<string | null>(initialId);
  // Compliance managers may reassign the owner in edit mode. (Global admins are
  // additionally allowed at the API layer; they hit the same PATCH.)
  const { isManager, userOid } = useComplianceAccess();
  const canReassign = isManager;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TabKey>('overview');
  // "More details" on the Obligations tab (DPA / insurance flags).
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [categories, setCategories] = useState<ContractCategory[]>([]);
  const [departments, setDepartments] = useState<ContractDepartment[]>([]);
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([]);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);
  // Portal target for TypeAhead dropdowns. Drawer uses dialog.showModal()
  // which renders in the browser top layer, invisible to document.body
  // portals. The drawer panel itself has `transform`, creating a
  // stacking context that traps position:fixed dropdowns — so portal
  // into the <dialog> element (parent of panel), which is in top layer
  // but has no transform of its own.
  const [dialogEl, setDialogEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) {
      setDialogEl(null);
      return;
    }
    // Drawer renders an open dialog via createPortal to document.body.
    // Poll briefly for it; the dialog mounts in the same tick but in a
    // sibling React tree.
    const find = () => {
      const el = document.querySelector('dialog[open]');
      if (el) setDialogEl(el as HTMLElement);
    };
    find();
    const id = window.setTimeout(find, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Reset + load whenever the drawer opens with new props. One effect
  // reading initialMode/initialId directly avoids a race where a stale
  // closure from a prior edit session overwrites a freshly reset create form.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setCurrentId(initialId);
    setTab('overview');
    setForm(emptyForm);
    setContract(null);
    setMoreDetailsOpen(false);
    if (initialMode === 'edit' && initialId) {
      (async () => {
        setLoading(true);
        try {
          const c = await contractsService.get(initialId);
          setContract(c);
          setForm(contractToFormState(c));
          setMoreDetailsOpen(c.dpa_required || c.dpa_executed || c.insurance_required);
        } catch (error: any) {
          const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
          setErrorNotification({ title: 'Failed to load contract', message: msg });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [open, initialMode, initialId]);

  // Load reference data (categories, third parties) when drawer opens.
  // adUsers come in as a prop from the parent so they're loaded once per
  // page visit rather than per drawer open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [cats, deps, tps] = await Promise.all([
          contractCategoriesService.list(),
          contractDepartmentsService.list(),
          thirdPartiesService.list(),
        ]);
        if (cancelled) return;
        setCategories(cats);
        setDepartments(deps);
        setThirdParties(tps);
      } catch (error: any) {
        const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
        if (!cancelled) {
          setErrorNotification({ title: 'Failed to load reference data', message: msg });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);


  const userOptions = useMemo<TypeAheadOption[]>(
    () =>
      adUsers
        .filter((u) => (u.email || '').toLowerCase().endsWith('@gwii.com'))
        .map((u) => ({
          value: u.user_id,
          label: u.display_name,
          description: u.email,
        })),
    [adUsers],
  );

  // New contracts are owned by whoever creates them (still changeable before
  // saving). Waits for the user's oid and the owner list, which can load
  // after the drawer opens, and only fills a still-blank Owner.
  useEffect(() => {
    if (!open || mode !== 'create' || !userOid) return;
    if (!userOptions.some((o) => o.value === userOid)) return;
    setForm((f) => (f.owner_oid === '' ? { ...f, owner_oid: userOid } : f));
  }, [open, mode, userOid, userOptions]);

  const thirdPartyOptions = useMemo<TypeAheadOption[]>(
    () =>
      thirdParties.map((tp) => ({
        value: tp.id,
        label: tp.name,
      })),
    [thirdParties],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Start Date / Contract Length edits fill in End Date — but only while it is
  // still blank, so a date the user typed is never overwritten.
  const setTermField = <K extends 'start_date' | 'term_length' | 'term_unit'>(
    key: K,
    value: FormState[K],
  ) =>
    setForm((f) => {
      const next = withMonthlyCostSuggestion(f, { ...f, [key]: value });
      if (next.end_date === '') {
        const end = endDateForTerm(next.start_date, Number(next.term_length), next.term_unit);
        if (end) next.end_date = end;
      }
      return next;
    });

  // Total / Length edits refresh the Monthly Cost suggestion until the user
  // types their own figure.
  const setContractValue = (value: string) =>
    setForm((f) => withMonthlyCostSuggestion(f, { ...f, contract_value: value }));

  const monthlySuggestion = suggestedMonthlyCost(
    form.contract_value,
    form.term_length,
    form.term_unit,
  );

  const termLengthNumber = Number(form.term_length);
  const hasTermLength = form.term_length.trim() !== '';
  const expectedEndDate = hasTermLength
    ? endDateForTerm(form.start_date, termLengthNumber, form.term_unit)
    : null;
  // Month-to-month (1 month + auto-renewal) is never Pending Renewal: the
  // option is hidden and a stored Pending Renewal shows (and saves) as Active.
  const monthToMonth = isMonthToMonth({
    term_length: hasTermLength ? termLengthNumber : null,
    term_unit: form.term_unit,
    auto_renewal: form.auto_renewal,
  });
  const shownStatus: ContractStatus =
    monthToMonth && form.status === 'pending_renewal' ? 'active' : form.status;
  const statusOptions = monthToMonth
    ? STATUS_OPTIONS.filter((s) => s.value !== 'pending_renewal')
    : STATUS_OPTIONS;

  const validate = (): string | null => {
    if (!form.title.trim()) return 'Title is required.';
    if (form.title.length > 255) return 'Title must be 255 characters or fewer.';
    if (!form.third_party_id) return 'Third Party is required.';
    if (!form.category) return 'Category is required.';
    if (!form.department) return 'Department is required.';
    if (!form.start_date) return 'Start Date is required.';
    if (!form.end_date) return 'End Date is required.';
    if (new Date(form.end_date) <= new Date(form.start_date))
      return 'End Date must be after Start Date.';
    if (hasTermLength && (!Number.isInteger(termLengthNumber) || termLengthNumber < 1))
      return 'Contract Length must be a whole number of 1 or more.';
    if (!form.owner_oid.trim())
      return 'Owner is required — pick one from the list.';
    if (form.auto_renewal && !form.notice_period_days)
      return 'Notice Period Days is required when Auto Renewal is enabled.';
    return null;
  };

  const buildCreatePayload = (): ContractCreate => ({
    third_party_id: form.third_party_id,
    title: form.title.trim(),
    contract_value: form.contract_value.trim() === '' ? null : form.contract_value.trim(),
    monthly_cost: form.monthly_cost.trim() === '' ? null : form.monthly_cost.trim(),
    recurrence: form.recurrence === '' ? null : form.recurrence,
    // Portal-wide rule: every contract is USD. Not user-editable.
    currency: 'USD',
    start_date: form.start_date,
    end_date: form.end_date,
    term_length: hasTermLength ? termLengthNumber : null,
    term_unit: hasTermLength ? form.term_unit : null,
    owner_oid: form.owner_oid.trim(),
    status: shownStatus,
    category: form.category,
    department: form.department,
    notes: form.notes.trim() === '' ? null : form.notes,
    notice_period_days:
      form.notice_period_days.trim() === '' ? null : Number(form.notice_period_days),
    auto_renewal: form.auto_renewal,
    renewal_action_date: form.renewal_action_date || null,
    renewal_owner_oid: form.renewal_owner_oid.trim() === '' ? null : form.renewal_owner_oid.trim(),
    termination_for_convenience: form.termination_for_convenience,
    termination_notice_days:
      form.termination_notice_days.trim() === '' ? null : Number(form.termination_notice_days),
    dpa_required: form.dpa_required,
    dpa_executed: form.dpa_executed,
    insurance_required: form.insurance_required,
  });

  const buildUpdatePayload = (): ContractUpdate => {
    // currency is a create-time constant (USD); leave existing rows untouched on edit.
    const { currency: _currency, ...payload } = buildCreatePayload();
    if (!canReassign) {
      // Non-managers cannot reassign — never send owner_oid on update.
      const { owner_oid: _ignored, ...rest } = payload;
      return rest;
    }
    return payload;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (contract?.is_archived) return;
    const err = validate();
    if (err) {
      setErrorNotification({ title: 'Validation Error', message: err });
      return;
    }
    if (
      mode === 'edit' &&
      currentId &&
      canReassign &&
      contract &&
      form.owner_oid.trim() !== contract.owner_oid
    ) {
      const nameFor = (oid: string) =>
        userOptions.find((o) => o.value === oid)?.label || oid;
      const ok = window.confirm(
        `Reassign owner from ${nameFor(contract.owner_oid)} to ${nameFor(form.owner_oid.trim())}?`,
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (mode === 'edit' && currentId) {
        const updated = await contractsService.update(currentId, buildUpdatePayload());
        setContract(updated);
        setForm(contractToFormState(updated));
        onSaved(updated);
        // Edits are done once saved — close rather than make the user hit X.
        onClose();
      } else {
        const created = await contractsService.create(buildCreatePayload());
        // Flip to edit mode but keep drawer open.
        setContract(created);
        setForm(contractToFormState(created));
        setCurrentId(created.id);
        setMode('edit');
        onSaved(created);
      }
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Save', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contract) return;
    if (!window.confirm(`Are you sure you want to delete "${contract.title}"?`)) return;
    try {
      await contractsService.remove(contract.id);
      onDeleted();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Delete', message: msg });
    }
  };

  const handleRestore = async () => {
    if (!contract) return;
    try {
      const restored = await contractsService.restore(contract.id);
      setContract(restored);
      setForm(contractToFormState(restored));
      onSaved(restored);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to Restore', message: msg });
    }
  };

  const handleArchive = async (archive: boolean) => {
    if (!contract) return;
    setSaving(true);
    try {
      const updated = archive
        ? await contractsService.archive(contract.id)
        : await contractsService.unarchive(contract.id);
      setContract(updated);
      setForm(contractToFormState(updated));
      onSaved(updated);
      // Archiving is the last thing you do with a contract; unarchiving is
      // usually so you can edit it, so that one stays open.
      if (archive) onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: archive ? 'Failed to Archive' : 'Failed to Unarchive', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const isEdit = mode === 'edit';
  const documentsDisabled = !isEdit || !currentId;
  // Archived contracts are read-only until unarchived (the API refuses edits).
  const archived = isEdit && !!contract?.is_archived;
  const locked = saving || archived;
  // Same rule as the backend: managers any contract, others their own.
  const canArchiveOrUnarchive = !!contract && (isManager || contract.owner_oid === userOid);
  const canArchive =
    isEdit && !!contract && !contract.is_deleted && !archived && canArchiveOrUnarchive &&
    (contract.status === 'expired' || contract.status === 'terminated');
  const archivedByName = contract?.archived_by_oid
    ? adUsers.find((u) => u.user_id === contract.archived_by_oid)?.display_name ||
      contract.archived_by_oid
    : null;

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white';
  const labelCls = 'block text-sm font-medium text-label mb-1';

  const title = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="truncate max-w-md">
        {isEdit && contract ? contract.title : 'New Contract'}
      </span>
      {isEdit && contract && (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[effectiveStatus(contract)] || ''}`}
        >
          {effectiveStatus(contract).replace('_', ' ')}
        </span>
      )}
      {archived && (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          archived
        </span>
      )}
      {isEdit && contract?.is_deleted && (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200">
          deleted
        </span>
      )}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div>
        {!isEdit && onBulkImport && (
          <button
            type="button"
            onClick={onBulkImport}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <ArrowUpTrayIcon className="w-4 h-4" /> Bulk Import
          </button>
        )}
        {isEdit && contract && (
          contract.is_deleted ? (
            <button
              type="button"
              onClick={handleRestore}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" /> Restore
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <TrashIcon className="w-4 h-4" /> Delete
            </button>
          )
        )}
        {canArchive && (
          <button
            type="button"
            onClick={() => handleArchive(true)}
            disabled={saving}
            className="ml-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <ArchiveBoxIcon className="w-4 h-4" /> Archive
          </button>
        )}
        {archived && canArchiveOrUnarchive && (
          <button
            type="button"
            onClick={() => handleArchive(false)}
            disabled={saving}
            className="ml-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <ArchiveBoxXMarkIcon className="w-4 h-4" /> Unarchive
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          Cancel
        </button>
        {!archived && (
        <button
          type="button"
          onClick={() => handleSubmit()}
          disabled={saving || loading}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-4 h-4" />
              {isEdit ? 'Save Changes' : 'Create Contract'}
            </>
          )}
        </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Drawer
        isOpen={open}
        onClose={onClose}
        title={title}
        width="50vw"
        footer={footer}
        disableClickOutside
        contentClassName="relative flex-1 overflow-y-auto flex flex-col"
      >
        {/* Tab bar */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 pt-3 flex-wrap bg-theme-bg-secondary sticky top-0 z-10">
          {TABS.map((k) => {
            const disabled = k === 'documents' && documentsDisabled;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  setTab(k);
                }}
                disabled={disabled}
                title={disabled ? 'Save first' : undefined}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === k
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : disabled
                      ? 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {TAB_LABELS[k]}
                {k === 'documents' && disabled && (
                  <span className="ml-1 text-xs">(save first)</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 px-4 sm:px-6 py-4 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {archived && contract && (
                <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                  Archived on {formatDateOnly(contract.archived_at?.slice(0, 10))}
                  {archivedByName ? ` by ${archivedByName}` : ''}. Unarchive it to make changes.
                </div>
              )}
              {tab === 'overview' && (
                <div className="space-y-6">
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Identity
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>
                          Third Party <span className="text-red-500">*</span>
                        </label>
                        <TypeAhead
                          options={thirdPartyOptions}
                          value={form.third_party_id}
                          onChange={(value) => set('third_party_id', value)}
                          placeholder="Search for a third party..."
                          onClear={() => set('third_party_id', '')}
                          disabled={locked}
                          portalContainer={dialogEl}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={form.title}
                          onChange={(e) => set('title', e.target.value)}
                          maxLength={255}
                          className={inputCls}
                          disabled={locked}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>
                          Category <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={form.category}
                          onChange={(e) => set('category', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        >
                          <option value="">Select...</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>
                          Department <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={form.department}
                          onChange={(e) => set('department', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        >
                          <option value="">Select...</option>
                          {departments.map((d) => (
                            <option key={d.id} value={d.name}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="contract-status" className={labelCls}>Status</label>
                        <select
                          id="contract-status"
                          value={shownStatus}
                          onChange={(e) => set('status', e.target.value as ContractStatus)}
                          className={inputCls}
                          disabled={locked}
                        >
                          {statusOptions.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Financial
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contract-total-value" className={labelCls}>
                          Total Contract Value (USD)
                        </label>
                        <input
                          id="contract-total-value"
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.contract_value}
                          onChange={(e) => setContractValue(e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          The full amount over the life of the contract.
                        </p>
                      </div>
                      <div>
                        <label htmlFor="contract-monthly-cost" className={labelCls}>
                          Monthly Cost (USD)
                        </label>
                        <input
                          id="contract-monthly-cost"
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.monthly_cost}
                          onChange={(e) => set('monthly_cost', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {monthlySuggestion && form.monthly_cost !== monthlySuggestion
                            ? `Total ÷ Contract Length would be $${monthlySuggestion}.`
                            : 'What the contract costs each month. Filled in from Total ÷ Contract Length; change it if billing differs.'}
                        </p>
                      </div>
                      <div>
                        <label htmlFor="contract-billing-cadence" className={labelCls}>
                          Billing Cadence
                        </label>
                        <select
                          id="contract-billing-cadence"
                          value={form.recurrence}
                          onChange={(e) =>
                            set('recurrence', e.target.value as '' | ContractRecurrence)
                          }
                          className={inputCls}
                          disabled={locked}
                        >
                          <option value="">One-time</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                          <option value="annually">Annually</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          How often we&apos;re billed on this contract.
                        </p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Dates
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contract-start-date" className={labelCls}>
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contract-start-date"
                          type="date"
                          value={form.start_date}
                          onChange={(e) => setTermField('start_date', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                      </div>
                      <div>
                        <label htmlFor="contract-term-length" className={labelCls}>
                          Contract Length
                        </label>
                        <div className="flex gap-2">
                          <input
                            id="contract-term-length"
                            type="number"
                            min="1"
                            step="1"
                            placeholder="e.g. 12"
                            value={form.term_length}
                            onChange={(e) => setTermField('term_length', e.target.value)}
                            className={`${inputCls} w-24`}
                            disabled={locked}
                          />
                          <select
                            aria-label="Contract Length unit"
                            value={form.term_unit}
                            onChange={(e) =>
                              setTermField('term_unit', e.target.value as ContractTermUnit)
                            }
                            className={inputCls}
                            disabled={locked}
                          >
                            <option value="months">Months</option>
                            <option value="years">Years</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="contract-end-date" className={labelCls}>
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="contract-end-date"
                          type="date"
                          value={form.end_date}
                          onChange={(e) => set('end_date', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                      </div>
                      <div>
                        <label htmlFor="contract-renewal-action-date" className={labelCls}>
                          Renewal Action Date
                        </label>
                        <input
                          id="contract-renewal-action-date"
                          type="date"
                          value={form.renewal_action_date}
                          onChange={(e) => set('renewal_action_date', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                      </div>
                    </div>
                    {expectedEndDate && form.end_date && form.end_date !== expectedEndDate && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        A {formatTermLength(termLengthNumber, form.term_unit)} contract from this
                        Start Date ends {expectedEndDate}.{' '}
                        <button
                          type="button"
                          onClick={() => set('end_date', expectedEndDate)}
                          className="text-accent underline hover:no-underline"
                          disabled={locked}
                        >
                          Use that End Date
                        </button>
                      </p>
                    )}
                  </section>

                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                      Ownership
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className={labelCls}>
                          Owner <span className="text-red-500">*</span>
                        </label>
                        <TypeAhead
                          options={userOptions}
                          value={form.owner_oid}
                          onChange={(value) => set('owner_oid', value)}
                          placeholder="Search for owner..."
                          ariaLabel="Owner"
                          showDescriptions
                          disabled={locked || (isEdit && !canReassign)}
                          portalContainer={dialogEl}
                        />
                        {isEdit && !canReassign && (
                          <p className="text-xs text-gray-500 mt-1">
                            Owner cannot be changed after creation.
                          </p>
                        )}
                        {isEdit && canReassign && (
                          <p className="text-xs text-gray-500 mt-1">
                            Reassign the owner (e.g., when someone leaves). You'll be asked to confirm.
                          </p>
                        )}
                      </div>
                      <div>
                        <TypeAhead
                          options={userOptions}
                          value={form.renewal_owner_oid || ''}
                          onChange={(value) => set('renewal_owner_oid', value)}
                          placeholder="Search for renewal owner..."
                          label="Renewal Owner"
                          showDescriptions
                          disabled={locked}
                          onClear={() => set('renewal_owner_oid', '')}
                          portalContainer={dialogEl}
                        />
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {tab === 'obligations' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Toggle
                          checked={form.auto_renewal}
                          onChange={(v) => set('auto_renewal', v)}
                          size="sm"
                          ariaLabel="Auto Renewal"
                          disabled={locked}
                        />
                        <span>Auto Renewal</span>
                        <InfoTip text="Renews automatically for another term unless notice is given before the notice deadline." />
                      </div>
                      <div>
                        <label htmlFor="contract-notice-days" className={labelCls}>
                          Notice Period Days
                          {form.auto_renewal && <span className="text-red-500"> *</span>}
                          <InfoTip text="Days before the End Date you must tell the vendor you're not renewing. The owner gets a reminder ahead of that deadline." />
                        </label>
                        <input
                          id="contract-notice-days"
                          type="number"
                          min="0"
                          value={form.notice_period_days}
                          onChange={(e) => set('notice_period_days', e.target.value)}
                          className={inputCls}
                          disabled={locked}
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Toggle
                          checked={form.termination_for_convenience}
                          onChange={(v) => set('termination_for_convenience', v)}
                          size="sm"
                          ariaLabel="Termination for Convenience"
                          disabled={locked}
                        />
                        <span>Termination for Convenience</span>
                        <InfoTip text="We can end the contract early without cause by giving the agreed notice." />
                      </div>
                      {form.termination_for_convenience && (
                        <div>
                          <label htmlFor="contract-termination-days" className={labelCls}>
                            Termination Notice Days
                            <InfoTip text="Days of notice needed to end the contract early. For reference only; no reminder is sent." />
                          </label>
                          <input
                            id="contract-termination-days"
                            type="number"
                            min="0"
                            value={form.termination_notice_days}
                            onChange={(e) => set('termination_notice_days', e.target.value)}
                            className={inputCls}
                            disabled={locked}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <details
                    open={moreDetailsOpen}
                    onToggle={(e) => setMoreDetailsOpen(e.currentTarget.open)}
                    className="rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <summary className="cursor-pointer select-none px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                      More details
                    </summary>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 pb-4 pt-2">
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Toggle
                          checked={form.dpa_required}
                          onChange={(v) => set('dpa_required', v)}
                          size="sm"
                          ariaLabel="DPA Required"
                          disabled={locked}
                        />
                        <span>DPA Required</span>
                        <InfoTip text="The vendor handles our data, so a Data Processing Agreement is needed." />
                      </div>
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Toggle
                          checked={form.dpa_executed}
                          onChange={(v) => set('dpa_executed', v)}
                          size="sm"
                          ariaLabel="DPA Executed"
                          disabled={locked}
                        />
                        <span>DPA Executed</span>
                        <InfoTip text="The DPA has been signed by both parties." />
                      </div>
                      <div className="inline-flex items-center gap-2 text-sm">
                        <Toggle
                          checked={form.insurance_required}
                          onChange={(v) => set('insurance_required', v)}
                          size="sm"
                          ariaLabel="Insurance Required"
                          disabled={locked}
                        />
                        <span>Insurance Required</span>
                        <InfoTip text="The vendor must carry insurance (e.g. cyber or general liability) under this contract." />
                      </div>
                    </div>
                  </details>
                </div>
              )}

              {tab === 'documents' && isEdit && currentId && (
                <ContractDocumentsPanel contractId={currentId} />
              )}

              {tab === 'notes' && !currentId && (
                <p className="text-sm text-muted">
                  Save the contract first to add notes.
                </p>
              )}
              {tab === 'notes' && currentId && (
                <ContractNotesPanel
                  contractId={currentId}
                  adUsers={adUsers}
                  legacyNote={contract?.notes ?? null}
                />
              )}

              {tab === 'audit' && isEdit && currentId && (
                <EntityAuditPanel entityType="contract" entityId={currentId} />
              )}

              {/* Hidden submit input so Enter within inputs triggers submit */}
              <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1}>
                <XMarkIcon className="w-0 h-0" />
              </button>
            </form>
          )}
        </div>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </>
  );
}
